// src/components/admin/AdminNavigation.jsx

import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  Music,
  Video,
  User,
  Users,
  Image as ImageIcon,
  ShoppingBag,
  Globe,
  LogOut,
  Lock
} from 'lucide-react';

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'music', label: 'Music', icon: Music },
  // { id: 'artist-management', label: 'Artists', icon: Users },
  // { id: 'approving', label: 'Approvals', icon: ShieldCheck },
  { id: 'videos', label: 'Videos', icon: Video },
  { id: 'members', label: 'Members', icon: User },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'gallery', label: 'Gallery', icon: ImageIcon },
  { id: 'purchases', label: 'Purchases', icon: ShoppingBag },
];

export default function AdminNavigation({ currentUser, onLogout }) {
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'dashboard';

  const renderItems = (compact) =>
    NAV_ITEMS.map((item) => {
      const Icon = item.icon;
      const isActive = activeTab === item.id;
      return (
        <Link
          key={item.id}
          to={`/admin?tab=${item.id}`}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition whitespace-nowrap ${
            isActive
              ? 'bg-[#bd9f67] text-[#243137]'
              : 'text-[#a6a6b5] hover:text-white hover:bg-white/5 border border-transparent'
          }`}
        >
          <Icon size={compact ? 12 : 14} />
          {item.label}
        </Link>
      );
    });

  return (
    <motion.nav
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="fixed top-0 w-full z-50 bg-[#0d0d11]/95 backdrop-blur-md border-b border-[#2a2a33] shadow-lg"
    >
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-500 via-fuchsia-400 to-cyan-400"></div>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
        {/* Desktop */}
        <div className="hidden lg:flex items-center justify-between gap-4">
          <Link to="/admin?tab=dashboard" className="flex items-center gap-2 flex-shrink-0">
            <img
              src="/images/asidors_logo_white.png"
              alt="The Asidors"
              className="h-9 hover:opacity-80 transition"
            />
            <span className="text-white font-semibold text-sm tracking-wide">Admin</span>
          </Link>

          <div className="flex items-center gap-1 overflow-x-auto">
            {renderItems(false)}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to="/"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-[#a6a6b5] hover:text-white hover:bg-white/5 transition"
            >
              <Globe size={14} />
              View Site
            </Link>
            <span className="flex items-center gap-1.5 px-3 py-2 text-xs text-[#a6a6b5] max-w-[140px] truncate">
              <Lock size={12} />
              {currentUser?.displayName || 'Admin'}
            </span>
            <button
              onClick={onLogout}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-red-300 hover:bg-red-500/10 transition"
            >
              <LogOut size={14} />
              Logout
            </button>
          </div>
        </div>

        {/* Mobile */}
        <div className="lg:hidden flex items-center justify-between gap-2">
          <Link to="/admin?tab=dashboard" className="flex items-center gap-2">
            <img
              src="/images/asidors_logo_white.png"
              alt="The Asidors"
              className="h-8 hover:opacity-80 transition"
            />
            <span className="text-white font-semibold text-xs tracking-wide">Admin</span>
          </Link>
          <div className="flex items-center gap-1">
            <Link
              to="/"
              className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-medium text-[#a6a6b5] hover:text-white hover:bg-white/5 transition"
            >
              <Globe size={13} />
              Site
            </Link>
            <button
              onClick={onLogout}
              className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-medium text-red-300 hover:bg-red-500/10 transition"
            >
              <LogOut size={13} />
            </button>
          </div>
        </div>
        <div className="lg:hidden mt-2 flex items-center gap-1 overflow-x-auto pb-1 -mx-1 px-1">
          {renderItems(true)}
        </div>
      </div>
    </motion.nav>
  );
}
